import React, { useState, useEffect } from "react";
import {
  CreateMailServerDto,
  MailServer,
  UpdateMailServerDto,
} from "../dto/smpt.dto";
import { createMailServer, updateMailServer } from "../../../server/server";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export enum MailServerPurpose {
  REGISTRATION = "registration",
  PASSWORD_RESET = "password_reset",
  CONTACT = "contact",
  FALLBACK = "fallback",
}

interface AddMailServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingServer?: MailServer | null;
}

const AddMailServerModal: React.FC<AddMailServerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingServer,
}) => {
  const [formData, setFormData] = useState<CreateMailServerDto>({
    name: "",
    host: "",
    port: 587,
    username: "",
    password: "",
    from: "",
    purpose: "registration" as MailServerPurpose,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (editingServer) {
      setFormData({
        name: editingServer.name,
        host: editingServer.host,
        port: editingServer.port,
        username: editingServer.username,
        password: "",
        from: editingServer.from,
        purpose: editingServer.purpose,
      });
    } else {
      setFormData({
        name: "",
        host: "",
        port: 587,
        username: "",
        password: "",
        from: "",
        purpose: "registration" as MailServerPurpose,
      });
    }
    setError(null);
  }, [editingServer, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any,
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: name === "port" ? parseInt(value) || 0 : value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError("Назив је обавезан");
      return false;
    }
    if (!formData.host.trim()) {
      setError("Хост је обавезан");
      return false;
    }
    if (!formData.port || formData.port <= 0) {
      setError("Важећи број порта је обавезан");
      return false;
    }
    if (!formData.username.trim()) {
      setError("Корисничко име је обавезно");
      return false;
    }
    if (!editingServer && !formData.password.trim()) {
      setError("Лозинка је обавезна");
      return false;
    }
    if (!formData.from.trim()) {
      setError("Емаил пошиљаоца је обавезан");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.from)) {
      setError("Важећа емаил адреса је обавезна за поље Пошиљалац");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      if (editingServer) {
        const updateData: UpdateMailServerDto = {
          id: editingServer.id,
          name: formData.name,
          host: formData.host,
          port: formData.port,
          username: formData.username,
          from: formData.from,
          purpose: formData.purpose,
        };

        if (formData.password.trim()) {
          updateData.password = formData.password;
        }

        const response = await updateMailServer(editingServer.id, updateData);
        if (response.status === 200 || response.status === 201) {
          onSave();
        } else {
          setError("Неуспешно ажурирање СМТП сервера");
        }
      } else {
        const response = await createMailServer(formData);
        if (response.status === 200 || response.status === 201) {
          onSave();
        } else {
          setError("Неуспешно креирање СМТП сервера");
        }
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError("Сервер за ову сврху већ постоји");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Дошло је до грешке приликом чувања СМТП сервера");
      }
    } finally {
      setLoading(false);
    }
  };

  const commonPorts = [
    { value: 25, label: "25 (SMTP)" },
    { value: 465, label: "465 (SMTPS)" },
    { value: 587, label: "587 (SMTP TLS)" },
    { value: 2525, label: "2525 (Alternative)" },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl leading-6 font-medium">
                  {editingServer ? "Измени СМТП сервер" : "Додај СМТП сервер"}
                </h2>
              </div>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <TextField
                    fullWidth
                    size="small"
                    label="Назив"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Production SMTP Server"
                    error={!!error && !formData.name.trim()}
                    helperText={
                      !formData.name.trim() && error ? "Назив је обавезан" : ""
                    }
                  />
                </div>

                {/* Purpose */}
                <div>
                  <FormControl fullWidth size="small">
                    <InputLabel id="purpose-label">Сврха</InputLabel>
                    <Select
                      labelId="purpose-label"
                      id="purpose"
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleInputChange}
                      label="Purpose"
                    >
                      <MenuItem value="registration">Регистрација</MenuItem>
                      <MenuItem value="password_reset">
                        Ресетовање лозинке
                      </MenuItem>
                      <MenuItem value="contact">Контакт</MenuItem>
                      <MenuItem value="fallback">Резервни</MenuItem>
                    </Select>
                    <FormHelperText>
                      Само један сервер по сврси је дозвољен
                    </FormHelperText>
                  </FormControl>
                </div>

                {/* Host and Port */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <TextField
                      fullWidth
                      size="small"
                      label="Хост"
                      id="host"
                      name="host"
                      value={formData.host}
                      onChange={handleInputChange}
                      placeholder="smtp.gmail.com"
                      error={!!error && !formData.host.trim()}
                      helperText={
                        !formData.host.trim() && error ? "Хост је обавезан" : ""
                      }
                    />
                  </div>
                  <div>
                    <FormControl fullWidth size="small">
                      <InputLabel id="port-label">Порт</InputLabel>
                      <Select
                        labelId="port-label"
                        id="port"
                        name="port"
                        value={formData.port}
                        onChange={handleInputChange}
                        label="Порт"
                      >
                        {commonPorts.map((port) => (
                          <MenuItem key={port.value} value={port.value}>
                            {port.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Username */}
                <div>
                  <TextField
                    fullWidth
                    size="small"
                    label="Корисничко име"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="your-email@domain.com"
                    error={!!error && !formData.username.trim()}
                    helperText={
                      !formData.username.trim() && error
                        ? "Корисничко име је обавезно"
                        : ""
                    }
                  />
                </div>

                {/* Password */}
                <div>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Лозинка ${editingServer ? "(оставите празно да задржите тренутну)" : "*"}`}
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={
                      editingServer
                        ? "Унесите нову лозинку за промену"
                        : "Унесите лозинку"
                    }
                    error={
                      !!error && !editingServer && !formData.password.trim()
                    }
                    helperText={
                      !editingServer && !formData.password.trim() && error
                        ? "Лозинка је обавезна"
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {/* From Email */}
                <div>
                  <TextField
                    fullWidth
                    size="small"
                    label="Пошиљалац"
                    id="from"
                    name="from"
                    value={formData.from}
                    onChange={handleInputChange}
                    placeholder="noreply@yourdomain.com"
                    error={!!error && !formData.from.trim()}
                    helperText={
                      !formData.from.trim() && error
                        ? "Пошиљалац је обавезан"
                        : "Ово ће бити коришћено као адреса пошиљаоца за имејлове"
                    }
                  />
                </div>
              </div>
              <div className="flex flex-row justify-between items-center w-full mt-6">
                <div
                  className="border border-[#81848f] hover:bg-[#81848f] hover:text-white cursor-pointer flex items-center justify-center border-redError text-redError text-base font-semibold rounded-lg w-[200px] h-[55px] gap-2"
                  onClick={() => onClose()}
                >
                  Oткажи
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-darkBlue ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:opacity-90"
                  } flex items-center justify-center text-white text-base font-semibold rounded-lg w-[200px] h-[55px]`}
                >
                  {loading ? "Чување..." : "Сачувај"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMailServerModal;
